import { useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import clsx from 'clsx'
import { observer } from 'mobx-react-lite'

import searchTour from 'entities/tour/models/search-tour/search-tour'
import {
  AgesTour,
  AgesTourType,
  maxAgesChild,
  minAgesChild,
} from 'entities/tour/types'

import { AppIcon } from 'shared/ui/app-icon'
import { AllIcons } from 'shared/ui/app-icon/types.ts'
import { ButtonContinue } from 'shared/ui/bottom-continue'
import { Button } from 'shared/ui/button'
import { ButtonInputValue } from 'shared/ui/button-input-value'
import { HeaderGoBack } from 'shared/ui/header-go-back'
import { Paper } from 'shared/ui/paper'

import s from './styles.module.scss'
import { ChangeAgesChild, ChangeCountAges, MinMaxValue } from './types'

type AgesCounter = Record<AgesTourType, number>

const SelectPeoplePage = observer(() => {
  const navigate = useNavigate()
  const { t } = useTranslation()

  const searchTourPassenger =
    searchTour.getEditableDataForSearchTour()?.passengers

  const [ages, setAges] = useState<AgesCounter>({
    adults: searchTourPassenger?.adults || 1,
    children: searchTourPassenger?.children || 0,
  })

  const [agesChildren, setAgesChildren] = useState<number[]>(
    searchTourPassenger?.childrenAges || [],
  )

  const [showChildrenBlock, setShowChildrenBlock] = useState<boolean>(
    !!searchTourPassenger?.children && searchTourPassenger.children > 0,
  )

  const onSubmit = () => {
    searchTour.setPassengersCount({
      adults: ages.adults,
      children: ages.children,
      childrenAges: agesChildren,
    })

    navigate(-1)
  }

  const getMaxCountByAge = ({ name }: MinMaxValue) => {
    if (name === AgesTour.adults.key) {
      return AgesTour.adults.maxCount
    } else {
      return AgesTour.children.maxCount
    }
  }

  const getMinCountByAge = ({ name }: MinMaxValue) => {
    if (name == AgesTour.adults.key) {
      return 1
    }

    return 0
  }

  const onChangeCountAges = ({ key, value }: ChangeCountAges) => {
    if (key === AgesTour.children.key) {
      setAgesChildren((prev) => {
        if (value < prev.length) return prev.slice(0, value)

        if (value > prev.length)
          return [...prev, ...Array(value - prev.length).fill(minAgesChild)]

        return prev
      })

      if (value < 1) {
        setShowChildrenBlock(false)
        setAgesChildren([])
      } else if (value >= 1 && !showChildrenBlock) {
        setShowChildrenBlock(true)
      }
    }

    setAges((prev) => ({ ...prev, [key]: value }))
  }

  const onChangeAgesChildren = ({ value, index }: ChangeAgesChild) => {
    const ages = [...agesChildren]

    ages[index] = value

    setAgesChildren(ages)
  }

  const handleAddChild = () => {
    setShowChildrenBlock(true)
    setAges((prev) => ({ ...prev, children: 1 }))
    setAgesChildren([minAgesChild])
  }

  // const isActiveReset = useMemo(() => {
  //   return (
  //     ages?.adults !== AgesTour?.adults?.defaultValue ||
  //     ages?.children > AgesTour?.children?.defaultValue
  //   )
  // }, [ages])

  // const onReset = () => {
  //   setAges({
  //     children: AgesTour.children.defaultValue,
  //     adults: AgesTour.adults?.defaultValue,
  //   })
  //
  //   setAgesChildren([])
  // }

  return (
    <div className={clsx('container')}>
      <HeaderGoBack
        buttonWhite
        title={t('tours_block.who_going')}
        variant="grey"
      />

      <div className={s.countPassenger}>
        <Paper padding={[12, 16]}>
          <ButtonInputValue
            ageType={AgesTour.adults.key}
            title={t(AgesTour.adults.name)}
            subTitle={t(AgesTour.adults.rangeDescription)}
            value={ages.adults}
            minNumber={getMinCountByAge({
              state: ages,
              name: AgesTour.adults.key,
            })}
            maxNumber={getMaxCountByAge({
              state: ages,
              name: AgesTour.adults.key,
            })}
            onChange={(value) =>
              onChangeCountAges({
                value: value,
                key: AgesTour.adults.key,
              })
            }
          />
        </Paper>

        {!showChildrenBlock && (
          <Button
            variant={'transparent'}
            className={s.addChildButton}
            onClick={handleAddChild}
            type="button"
          >
            <AppIcon
              id={AllIcons.PLUS}
              height={24}
              width={24}
            />
            {t('tours_block.add_child')}
          </Button>
        )}

        {showChildrenBlock && (
          <Paper padding={[12, 16]}>
            <ButtonInputValue
              ageType={AgesTour.children.key}
              title={t(AgesTour.children.name)}
              subTitle={t(AgesTour.children.rangeDescription)}
              value={ages.children}
              minNumber={getMinCountByAge({
                state: ages,
                name: AgesTour.children.key,
              })}
              maxNumber={getMaxCountByAge({
                state: ages,
                name: AgesTour.children.key,
              })}
              onChange={(value) =>
                onChangeCountAges({
                  value: value,
                  key: AgesTour.children.key,
                })
              }
            />

            {!!agesChildren?.length && (
              <div className={s.additionalInfo}>
                <div className={s.wrapperAges}>
                  {agesChildren?.map((ageChild, indexChild) => (
                    <ButtonInputValue
                      variant="square"
                      value={ageChild}
                      minNumber={minAgesChild}
                      maxNumber={maxAgesChild}
                      title={t('tours_block.ages_children', {
                        count: indexChild + 1,
                      })}
                      onChange={(value) =>
                        onChangeAgesChildren({ value, index: indexChild })
                      }
                    />
                  ))}
                </div>

                <div className={s.info}>
                  {t('tours_block.child_age', {
                    count: AgesTour.children.maxCount,
                  })}
                </div>
              </div>
            )}
          </Paper>
        )}
      </div>

      <ButtonContinue onClick={onSubmit}>
        <span className={clsx('bold-16')}>{t('save')}</span>
      </ButtonContinue>
    </div>
  )
})

export default SelectPeoplePage
